import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import {
  dot,
  lights,
  min,
  mix,
  mx_noise_float,
  positionLocal,
  positionWorld,
  smoothstep,
  uniform,
  vec3,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { bakeMeshes } from '../render/bakeMeshes';
import { placeBone, solveLimb } from './limbIK';
import {
  CENTER_TO_EYE,
  CONTROLLER_GAP,
  PLAYER_CENTER_HEIGHT,
  type PlayerController,
} from './PlayerController';

type N = any;

/** The traveller's colours (linear RGB). */
const CLOTH: [number, number, number] = [0.05, 0.16, 0.17];
const SHIRT: [number, number, number] = [0.42, 0.37, 0.28];
const TROUSERS: [number, number, number] = [0.19, 0.15, 0.1];
const LEATHER: [number, number, number] = [0.16, 0.09, 0.045];
const BOOTS: [number, number, number] = [0.07, 0.05, 0.035];
const SKIN: [number, number, number] = [0.55, 0.33, 0.21];
const HAIR: [number, number, number] = [0.06, 0.04, 0.025];

/** Feet to hips, and hips to shoulders (m): the 1.8 m hero the capsule stands for. */
const HIP = 0.94;
const SHOULDER = 0.5;
/** Legs and arms: bone lengths (m), and where the joints sit. */
const THIGH = 0.43;
const SHIN = 0.44;
const UPPER_ARM = 0.29;
const FOREARM = 0.27;
const HIP_SIDE = 0.1;
const SHOULDER_SIDE = 0.2;
/** The eye, above the feet (m): where the view (and a held weapon's pose) is measured from. */
export const HERO_EYE = PLAYER_CENTER_HEIGHT + CONTROLLER_GAP + CENTER_TO_EYE;
/** Where the lantern hangs, from the hips (m): at the right hip, a little forward. */
const LAMP = new THREE.Vector3(0.22, 0.0, -0.1);
/**
 * The lantern's light on the hero: a share of the world's lantern, and no brighter than this
 * close (m). It hangs a hand's width from the hip, and the true 1/d² burned that side white;
 * this way the hero's lit side is about as bright as the pool of light at its feet.
 */
const LAMP_SHARE = 0.3;
const LAMP_NEAR = 0.5;
/** Light off that lit ground onto the hero (irradiance per cd of the lantern): its back isn't black. */
const LAMP_BOUNCE = 0.07;

const _q = new THREE.Quaternion();
const _yaw = new THREE.Quaternion();
const _Y = new THREE.Vector3(0, 1, 0);
const _up = new THREE.Vector3();
const _bounce = new THREE.Color();
const _m = new THREE.Matrix4();
const _sh = new THREE.Vector3();
const _hand = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _elbow = new THREE.Vector3();
const _hip = new THREE.Vector3();
const _foot = new THREE.Vector3();
const _knee = new THREE.Vector3();
const _dir = new THREE.Vector3();

type Look =
  | 'cloth'
  | 'shirt'
  | 'trousers'
  | 'boots'
  | 'skin'
  | 'leather'
  | 'hair'
  | 'metal'
  | 'lamp'
  | 'dark';

/** A unit-long limb piece along +Y from its origin (placeBone stretches it), tapering r0 → r1. */
function bone(r0: number, r1: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r1, r0, 1, 12, 1, true);
  g.translate(0, 0.5, 0);
  return g;
}

/** A body of revolution from (radius, height) pairs, squashed front to back by `depth`. */
function lathe(profile: [number, number][], depth: number, segments = 18): THREE.BufferGeometry {
  const g = new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r, y)),
    segments,
  );
  g.scale(1, 1, depth);
  g.computeVertexNormals();
  return g;
}

interface Limb {
  upper: THREE.Mesh;
  lower: THREE.Mesh;
  joint: THREE.Mesh;
  end: THREE.Object3D;
}

/**
 * The hero for the third-person view: a traveller in a teal tunic and hooded short cloak, with
 * a pack and bedroll, a belt with pouches, gloves and boots, and a lantern at the hip that glows
 * when it's lit. Arms and legs bend at elbows and knees. Walking, the legs stride and the arms
 * swing; in the air the legs tuck; floating, the limbs spread. Holding something (`held`,
 * `setHands`), the hands go to it. It faces where the player looks and stands on the player's
 * "up", so it turns with gravity too.
 *
 * It carries its own lights, which light nothing else: the bounce light around it (the world
 * sets `ambient` each frame, so it can fade it out down a cave; the lantern's own bounce off
 * the ground is added), the sun on it, and its lantern. They stay out of the scene, so showing
 * or hiding the hero never changes any other shader's lights.
 */
export class HeroModel {
  /** Everything drawn, placed relative to the camera by `update`. */
  readonly group = new THREE.Group();
  /**
   * What the hero holds (a weapon), in its own frame: feet at the origin, +Y up, −Z the way it
   * faces. The game puts the model in it and turns it each frame, with `setHands`.
   */
  readonly held = new THREE.Group();
  /** Universe position of the feet this frame. */
  readonly feet = new THREE.Vector3();
  /** Where the hero's lantern hangs this frame: universe, and relative to the camera. */
  readonly lantern = new THREE.Vector3();
  readonly lanternRender = new THREE.Vector3();
  /** The bounce light around the hero (irradiance, linear RGB). Set it before each update. */
  readonly ambient = new THREE.Color();
  /** The sun on the hero: the direction toward it, and its irradiance (black: none). Set them before each update. */
  readonly sunDirection = new THREE.Vector3(0, 1, 0);
  readonly sun = new THREE.Color(0, 0, 0);
  /** A lit lantern glows and lights the hero. */
  lanternLit = false;
  /** The lights its materials use (its own, see the class comment): for what it holds too. */
  readonly lightsNode: THREE.LightsNode;

  private readonly ambientLight = new THREE.AmbientLight(0xffffff, 1);
  private readonly sunLight = new THREE.DirectionalLight(0x000000, 1);
  private readonly lamp = new THREE.PointLight(0xffffff, 1, 0, 2);
  private readonly lampAt = uniform(new THREE.Vector3());
  /** The world's lantern: colour × intensity. */
  private readonly lampLight: THREE.Color;
  /** How brightly the lantern glows (0..1), eased. */
  private readonly glow = uniform(0);
  private readonly materials: Record<Look, THREE.MeshStandardNodeMaterial>;
  private readonly hips = new THREE.Group();
  private readonly torso = new THREE.Group();
  private readonly head = new THREE.Group();
  private readonly cloak = new THREE.Group();
  private readonly legs: Limb[] = [];
  private readonly arms: Limb[] = [];
  private readonly mainHand = new THREE.Vector3();
  private readonly offHand = new THREE.Vector3();
  private readonly handQ = new THREE.Quaternion();
  private holdMain = false;
  private holdOff = false;
  private phase = 0;
  private swing = 0;
  private tuck = 0;
  private spread = 0;

  /** `lampColor` and `lampIntensity` (cd): the lantern the world lights itself with. */
  constructor(lampColor: THREE.ColorRepresentation, lampIntensity: number) {
    this.group.name = 'hero';
    this.lampLight = new THREE.Color(lampColor).multiplyScalar(lampIntensity);
    // The lantern as a point light (E = I/d²), held at LAMP_NEAR's brightness closer in.
    const toLamp = positionWorld.sub(this.lampAt);
    const color = this.lampLight.clone().multiplyScalar(LAMP_SHARE);
    (this.lamp as unknown as { colorNode: N }).colorNode = vec3(color.r, color.g, color.b)
      .mul(this.glow)
      .mul(min(dot(toLamp, toLamp).div(LAMP_NEAR * LAMP_NEAR), 1));
    this.lightsNode = lights([this.ambientLight, this.sunLight, this.lamp]);
    const p: N = positionLocal;
    /** A material with its own fine grain: woven cloth, worn leather, skin. */
    const make = (rgb: [number, number, number], roughness: number, grain: number, scale: number) => {
      const m = new THREE.MeshStandardNodeMaterial({ roughness, metalness: 0 });
      const n = mx_noise_float(p.mul(scale));
      m.colorNode = vec3(...rgb).mul(n.mul(grain).add(1));
      m.lightsNode = this.lightsNode;
      return m;
    };
    const cloth = make(CLOTH, 0.92, 0.12, 120);
    // Cloth: a weave, and folds that catch the light in long creases.
    cloth.colorNode = vec3(...CLOTH)
      .mul(
        mx_noise_float(p.mul(vec3(260, 70, 260)))
          .mul(0.07)
          .add(1),
      )
      .mul(
        smoothstep(-0.6, 0.6, mx_noise_float(p.mul(vec3(9, 2.5, 9))))
          .mul(0.25)
          .add(0.85),
      );
    const lamp = make([0.22, 0.17, 0.1], 0.6, 0, 1);
    lamp.emissiveNode = (vec3(1.0, 0.72, 0.36) as N).mul(this.glow).mul(4);
    const metal = make([0.33, 0.31, 0.28], 0.45, 0.1, 50);
    metal.metalness = 0.6;
    const skin = make(SKIN, 0.62, 0.05, 30);
    // A little colour in the cheeks and lips: warmer toward the front of the face.
    skin.colorNode = mix(vec3(...SKIN), vec3(0.6, 0.3, 0.22), smoothstep(0.02, 0.12, p.z.negate()).mul(0.3));
    this.materials = {
      cloth,
      shirt: make(SHIRT, 0.9, 0.08, 140),
      trousers: make(TROUSERS, 0.9, 0.1, 120),
      boots: make(BOOTS, 0.7, 0.15, 40),
      skin,
      leather: make(LEATHER, 0.72, 0.18, 35),
      hair: make(HAIR, 0.8, 0.3, 90),
      metal,
      lamp,
      dark: make([0.02, 0.02, 0.025], 0.3, 0, 1),
    };
    this.build();
    // What moves each frame stays apart; the rest merges into a mesh per material.
    for (const o of [this.hips, this.torso, this.head, this.cloak, this.held]) o.userData.moves = true;
    for (const l of [...this.legs, ...this.arms])
      for (const o of [l.upper, l.lower, l.joint, l.end]) o.userData.moves = true;
    bakeMeshes(this.group);
  }

  /**
   * Hands on what it holds (hero frame, see `held`): the main hand at `main`, the other at `off`
   * (null: it swings free), both turned by `q`. Pass null to let both arms swing free.
   */
  setHands(main: THREE.Vector3 | null, off: THREE.Vector3 | null, q: THREE.Quaternion | null): void {
    this.holdMain = main !== null;
    this.holdOff = off !== null;
    if (main) this.mainHand.copy(main);
    if (off) this.offHand.copy(off);
    if (q) this.handQ.copy(q);
  }

  /**
   * Pose for this frame, interpolated like the camera (`alpha` between the last two physics
   * steps), facing the view's yaw, and placed relative to the camera at `cameraUniverse`.
   */
  update(player: PlayerController, alpha: number, dt: number, cameraUniverse: THREE.Vector3): void {
    const body = player.interpolatedBody(alpha, _q);
    player.interpolatedPosition(alpha, this.feet);
    _up.copy(_Y).applyQuaternion(body);
    // The controller keeps the capsule a little off the ground: the boots go down to it.
    this.feet.addScaledVector(_up, -(PLAYER_CENTER_HEIGHT + CONTROLLER_GAP));
    this.group.quaternion.copy(body).multiply(_yaw.setFromAxisAngle(_Y, player.yaw));
    this.group.position.subVectors(this.feet, cameraUniverse);

    // Walk cycle from the speed along the ground, with longer strides at speed.
    const vUp = player.velocity.dot(player.up);
    const along = Math.sqrt(Math.max(0, player.velocity.lengthSq() - vUp * vUp));
    const walking = player.mode === 'walk';
    const onGround = walking && player.grounded;
    this.phase = (this.phase + (onGround ? (along * dt) / (1.5 + 0.28 * along) : 0)) % 1;
    const k = 1 - Math.exp(-dt * 10);
    this.swing += ((onGround ? Math.min(1, along / 9.5) : 0) - this.swing) * k;
    this.tuck += ((walking && !player.grounded ? 1 : 0) - this.tuck) * k;
    this.spread += ((player.mode === 'float' ? 1 : 0) - this.spread) * k;

    const turn = this.phase * Math.PI * 2;
    const stride = Math.min(1, this.swing * 4) * (0.22 + 0.5 * this.swing);
    // A little bob with each step, a lean into the stride, and the head follows the view.
    this.hips.position.y = HIP + Math.abs(Math.cos(turn)) * 0.035 * this.swing - 0.1 * this.tuck;
    this.torso.rotation.x = -0.1 * this.swing;
    this.head.rotation.x = Math.max(-0.6, Math.min(0.6, player.pitch * 0.5));
    // The cloak swings back with speed, and lifts as you fall.
    this.cloak.rotation.x = 0.12 * this.swing + 0.25 * this.tuck + 0.03 * Math.sin(turn * 2) * this.swing;
    this.hips.updateMatrix();
    this.torso.updateMatrix();
    _m.multiplyMatrices(this.hips.matrix, this.torso.matrix);

    // Legs: from each hip to a foot that strides along the ground (knees bend forward).
    for (const [i, leg] of this.legs.entries()) {
      const side = i === 0 ? -1 : 1;
      const s = Math.sin(turn + (i === 0 ? 0 : Math.PI));
      const lift = Math.max(0, Math.cos(turn + (i === 0 ? 0 : Math.PI))) * 0.14 * this.swing;
      _hip.set(side * HIP_SIDE, 0, 0).applyMatrix4(this.hips.matrix);
      _foot.set(
        side * (HIP_SIDE + 0.02 + 0.18 * this.spread),
        0.09 + lift + 0.3 * this.tuck,
        -s * stride * 0.45 - 0.12 * this.tuck,
      );
      _pole.copy(_hip).add(_dir.set(side * 0.05, 0, -1));
      solveLimb(_hip, _foot, THIGH, SHIN, _pole, _knee);
      placeBone(leg.upper, _hip, _knee);
      placeBone(leg.lower, _knee, _foot);
      leg.joint.position.copy(_knee);
      leg.end.position.copy(_foot);
      leg.end.rotation.set(-s * stride * 0.4 * (1 - this.tuck), 0, 0);
    }
    // Arms: to the held weapon's grip, or swinging against the legs.
    for (const [i, arm] of this.arms.entries()) {
      const side = i === 0 ? -1 : 1;
      _sh.set(side * SHOULDER_SIDE, SHOULDER, 0).applyMatrix4(_m);
      const holding = i === 1 ? this.holdMain : this.holdOff;
      if (holding) _hand.copy(i === 1 ? this.mainHand : this.offHand);
      else {
        const s = Math.sin(turn + (i === 0 ? Math.PI : 0)) * stride;
        _hand.set(side * (0.24 + 0.4 * this.spread), SHOULDER - 0.52 + 0.3 * this.spread, s * 0.4 - 0.04);
        _hand.applyMatrix4(_m);
      }
      // Elbows bend back and out (down and out when reaching forward).
      _pole.copy(_sh).add(_dir.set(side * 0.6, -0.5, holding ? 0.2 : 0.8));
      solveLimb(_sh, _hand, UPPER_ARM, FOREARM, _pole, _elbow);
      placeBone(arm.upper, _sh, _elbow);
      placeBone(arm.lower, _elbow, _hand);
      arm.joint.position.copy(_elbow);
      arm.end.position.copy(_hand);
      if (holding) arm.end.quaternion.copy(this.handQ);
      else arm.end.quaternion.setFromUnitVectors(_Y, _dir.subVectors(_elbow, _hand).normalize());
    }

    this.glow.value += ((this.lanternLit ? 1 : 0) - this.glow.value) * (1 - Math.exp(-dt * 8));
    _bounce.copy(this.lampLight).multiplyScalar(LAMP_BOUNCE * this.glow.value);
    this.ambientLight.color.copy(this.ambient).add(_bounce);
    // The sun: a directional light (the sun's far away; the hero is small). Out of the scene, so
    // its matrices are updated here; three takes its direction from position → target.
    this.sunLight.color.copy(this.sun);
    this.sunLight.position.copy(this.sunDirection);
    this.sunLight.target.position.set(0, 0, 0);
    this.sunLight.updateMatrixWorld();
    this.sunLight.target.updateMatrixWorld();
    this.lantern.copy(LAMP).add(this.hips.position).applyQuaternion(this.group.quaternion).add(this.feet);
    this.lanternRender.subVectors(this.lantern, cameraUniverse);
    // The lamp isn't in the scene (see the class comment), so nothing else updates its matrix.
    this.lamp.position.copy(this.lanternRender);
    this.lamp.updateMatrixWorld();
    this.lampAt.value.copy(this.lanternRender);
  }

  private add(parent: THREE.Object3D, geo: THREE.BufferGeometry, look: Look, x = 0, y = 0, z = 0) {
    const mesh = new THREE.Mesh(geo, this.materials[look]);
    mesh.position.set(x, y, z);
    // Always drawn when shown: the boot's shader warm-up must draw it from the eye, inside it.
    mesh.frustumCulled = false;
    parent.add(mesh);
    return mesh;
  }

  /** An arm or a leg: two bones and the joint between, and what's at its end. */
  private limb(
    upper: THREE.BufferGeometry,
    lower: THREE.BufferGeometry,
    joint: number,
    look: Look,
    end: THREE.Object3D,
  ): Limb {
    const limb = {
      upper: this.add(this.group, upper, look),
      lower: this.add(this.group, lower, look),
      joint: this.add(this.group, new THREE.SphereGeometry(joint, 12, 8), look),
      end,
    };
    this.group.add(end);
    return limb;
  }

  /** The figure, facing −Z (the way the camera looks) with +Y up and its right hand at +X. */
  private build(): void {
    this.group.add(this.hips);
    this.group.add(this.held);
    this.hips.position.y = HIP;
    this.hips.add(this.torso);

    // The tunic: a chest wider than the waist, a skirt flaring over the hips below the belt.
    this.add(
      this.torso,
      lathe(
        [
          [0.001, -0.02],
          [0.165, 0.0],
          [0.155, 0.12],
          [0.185, 0.3],
          [0.2, 0.42],
          [0.17, 0.52],
          [0.07, 0.6],
          [0.001, 0.61],
        ],
        0.68,
      ),
      'cloth',
    );
    this.add(
      this.hips,
      lathe(
        [
          [0.17, 0.04],
          [0.185, -0.08],
          [0.21, -0.22],
          [0.205, -0.24],
        ],
        0.72,
      ),
      'cloth',
    ).material = this.materials.cloth;
    // The shirt's collar at the neck, and the neck.
    this.add(this.torso, new THREE.CylinderGeometry(0.075, 0.09, 0.06, 14), 'shirt', 0, 0.58, 0);
    this.add(this.torso, new THREE.CylinderGeometry(0.05, 0.058, 0.1, 12), 'skin', 0, 0.63, 0);
    // A belt with a buckle and two pouches.
    this.add(this.hips, new THREE.CylinderGeometry(0.172, 0.172, 0.055, 18), 'leather', 0, 0.05, 0).scale.z =
      0.72;
    this.add(this.hips, new RoundedBoxGeometry(0.06, 0.05, 0.02, 2, 0.008), 'metal', 0, 0.05, -0.125);
    for (const s of [-1, 1]) {
      const pouch = this.add(
        this.hips,
        new RoundedBoxGeometry(0.09, 0.1, 0.05, 2, 0.015),
        'leather',
        s * 0.16,
        -0.02,
        -0.06,
      );
      pouch.rotation.y = s * 0.6;
    }
    // A pack with its flap and a rolled blanket on top.
    this.add(this.torso, new RoundedBoxGeometry(0.3, 0.36, 0.15, 3, 0.04), 'leather', 0, 0.32, 0.17);
    this.add(this.torso, new RoundedBoxGeometry(0.31, 0.1, 0.16, 2, 0.03), 'leather', 0, 0.47, 0.17);
    this.add(
      this.torso,
      new THREE.CylinderGeometry(0.06, 0.06, 0.38, 12),
      'shirt',
      0,
      0.56,
      0.17,
    ).rotation.z = Math.PI / 2;
    for (const s of [-1, 1]) {
      // Shoulder straps.
      const strap = this.add(
        this.torso,
        new THREE.BoxGeometry(0.035, 0.42, 0.012),
        'leather',
        s * 0.1,
        0.32,
        -0.125,
      );
      strap.rotation.x = 0.08;
    }

    // The hooded cloak over the shoulders, hanging behind to the backs of the knees.
    this.cloak.position.set(0, SHOULDER + 0.06, 0.06);
    this.torso.add(this.cloak);
    const cape = new THREE.CylinderGeometry(0.24, 0.34, 0.95, 16, 3, true, -Math.PI * 0.38, Math.PI * 0.76);
    cape.translate(0, -0.47, 0);
    cape.scale(1, 1, 0.7);
    const capeMesh = this.add(this.cloak, cape, 'cloth', 0, 0, 0.04);
    capeMesh.material = this.materials.cloth.clone();
    (capeMesh.material as THREE.MeshStandardNodeMaterial).side = THREE.DoubleSide;
    (capeMesh.material as THREE.MeshStandardNodeMaterial).lightsNode = this.lightsNode;
    this.add(this.cloak, new THREE.TorusGeometry(0.13, 0.035, 8, 20), 'cloth', 0, 0, -0.01).rotation.x =
      Math.PI / 2;

    // The head: face, nose, eyes, hair, and the hood down at the back.
    this.head.position.set(0, SHOULDER + 0.2, 0.0);
    this.torso.add(this.head);
    const skull = this.add(this.head, new THREE.SphereGeometry(0.105, 20, 16), 'skin');
    skull.scale.set(0.92, 1.08, 1);
    const jaw = this.add(this.head, new THREE.SphereGeometry(0.075, 16, 12), 'skin', 0, -0.06, -0.02);
    jaw.scale.set(1, 0.8, 1);
    const nose = this.add(this.head, new THREE.ConeGeometry(0.016, 0.04, 8), 'skin', 0, -0.005, -0.1);
    nose.rotation.x = -Math.PI / 2 + 0.3;
    for (const s of [-1, 1]) {
      this.add(this.head, new THREE.SphereGeometry(0.011, 8, 6), 'dark', s * 0.036, 0.02, -0.088);
      const brow = this.add(
        this.head,
        new THREE.BoxGeometry(0.035, 0.008, 0.012),
        'hair',
        s * 0.038,
        0.038,
        -0.088,
      );
      brow.rotation.z = s * -0.15;
      this.add(this.head, new THREE.SphereGeometry(0.022, 8, 6), 'skin', s * 0.1, 0.0, 0.0).scale.set(
        0.5,
        1,
        0.8,
      );
    }
    const hair = this.add(
      this.head,
      new THREE.SphereGeometry(0.112, 20, 14, 0, Math.PI * 2, 0, Math.PI * 0.55),
      'hair',
      0,
      0.012,
      0.008,
    );
    hair.rotation.x = 0.35;
    const hood = this.add(
      this.head,
      new THREE.SphereGeometry(0.15, 18, 12, Math.PI * 0.15, Math.PI * 0.7, Math.PI * 0.3, Math.PI * 0.5),
      'cloth',
      0,
      -0.1,
      0.06,
    );
    hood.rotation.y = Math.PI;
    hood.scale.set(1, 0.8, 1);

    // Legs: trousers to the knee and shin, boots with a sole and a turned-down cuff.
    for (let i = 0; i < 2; i++) {
      const boot = new THREE.Group();
      this.add(boot, new RoundedBoxGeometry(0.11, 0.12, 0.25, 3, 0.04), 'boots', 0, -0.03, -0.045);
      this.add(boot, new RoundedBoxGeometry(0.12, 0.025, 0.27, 2, 0.01), 'dark', 0, -0.09, -0.045);
      this.add(boot, new THREE.CylinderGeometry(0.07, 0.065, 0.06, 12), 'boots', 0, 0.06, 0);
      this.legs.push(this.limb(bone(0.095, 0.076), bone(0.072, 0.055), 0.07, 'trousers', boot));
    }
    // Arms: sleeves, leather bracers, gloved hands closed round a grip.
    for (let i = 0; i < 2; i++) {
      const hand = new THREE.Group();
      this.add(hand, new RoundedBoxGeometry(0.075, 0.095, 0.085, 2, 0.022), 'leather');
      this.add(hand, new THREE.CapsuleGeometry(0.014, 0.035, 3, 6), 'leather', 0, 0.03, 0.03).rotation.z =
        Math.PI / 2;
      const arm = this.limb(bone(0.055, 0.047), bone(0.045, 0.036), 0.05, 'cloth', hand);
      const bracer = new THREE.Mesh(
        new THREE.CylinderGeometry(0.044, 0.041, 0.4, 12, 1, true),
        this.materials.leather,
      );
      bracer.position.y = 0.75;
      bracer.frustumCulled = false;
      arm.lower.add(bracer);
      this.arms.push(arm);
    }
    // Pauldron-like shoulder pads under the cloak.
    for (const s of [-1, 1]) {
      const pad = this.add(
        this.torso,
        new THREE.SphereGeometry(0.075, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2),
        'leather',
        s * SHOULDER_SIDE,
        SHOULDER - 0.01,
        0,
      );
      pad.rotation.z = -s * 0.5;
    }

    // The lantern at the right hip: a glowing body between two caps, on a hook.
    const lamp = new THREE.Group();
    lamp.position.copy(LAMP);
    this.hips.add(lamp);
    this.add(lamp, new THREE.CylinderGeometry(0.045, 0.045, 0.11, 10), 'lamp');
    this.add(lamp, new THREE.CylinderGeometry(0.055, 0.05, 0.02, 10), 'metal', 0, 0.065, 0);
    this.add(lamp, new THREE.CylinderGeometry(0.05, 0.055, 0.02, 10), 'metal', 0, -0.065, 0);
    this.add(lamp, new THREE.TorusGeometry(0.025, 0.005, 6, 12), 'metal', 0, 0.09, 0);
  }
}
